import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, Monitor, Zap, Music } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

const EquiposDestacados = () => {
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEquipos = async () => {
      try {
        const response = await apiService.getEquiposDestacados(5);
        if (response.estado === 1) {
          setEquipos(response.data);
        }
      } catch (error) {
        console.error('Error cargando equipos destacados:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEquipos();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-black flex justify-center">
        <motion.div className="w-12 h-12 border-4 border-yellow-600 border-t-yellow-500 rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
      </section>
    );
  }

  if (equipos.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-black relative overflow-hidden">
      {/* Decoración de fondo */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent" />
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Encabezado */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="text-center mb-16"
        >
          <span className="inline-block text-yellow-500 text-sm font-semibold tracking-widest uppercase mb-3 border-b border-yellow-600/50 pb-1">
            Tecnología de Punta
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Equipos <span className="text-yellow-500">Destacados</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Descubre la maquinaria de alto rendimiento que hace posible eventos inolvidables.
          </p>
        </motion.div>

        {/* Grid de Equipos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {equipos.map((equipo, index) => (
            <motion.div
              key={equipo.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <Link to={`/equipo/${equipo.id}`} className="group block h-full">
                <div className="relative h-full bg-neutral-900/40 border border-yellow-600/20 rounded-2xl overflow-hidden hover:border-yellow-500/60 transition-all duration-500 hover:shadow-[0_0_40px_-10px_rgba(234,179,8,0.15)] flex flex-col">
                  
                  {/* Media (Video o Imagen) */}
                  <div className="relative aspect-video overflow-hidden bg-black">
                    {equipo.imagen_principal_es_video ? (
                      <video 
                        src={equipo.imagen_principal_url} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        autoPlay muted loop playsInline
                      />
                    ) : (
                      <img 
                        src={equipo.imagen_principal_url} 
                        alt={equipo.nombre} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    
                    {/* Overlay y Badge de Categoría */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80" />
                    <div className="absolute top-4 left-4">
                      <span className="bg-yellow-600/90 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                        {equipo.categoria_nombre}
                      </span>
                    </div>

                    {/* Icono de Play si es video */}
                    {equipo.imagen_principal_es_video && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-14 h-14 bg-yellow-500/90 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <Play className="w-6 h-6 text-black ml-1" fill="black" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contenido de la tarjeta */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-500 transition-colors line-clamp-2">
                      {equipo.nombre}
                    </h3>
                    <p className="text-gray-400 text-sm mb-6 line-clamp-2 flex-grow">
                      {equipo.descripcion_corta}
                    </p>
                    
                    <div className="flex items-center text-yellow-500 font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
                      <span>Ver detalles</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Botón Ver Todos (Opcional) */}
        <motion.div 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          viewport={{ once: true }} 
          className="text-center mt-12"
        >
          <Link 
            to="/equipos" 
            className="inline-flex items-center gap-2 border border-yellow-600/50 text-yellow-500 px-8 py-3 rounded-full font-semibold hover:bg-yellow-600/10 transition-all duration-300"
          >
            Ver catálogo completo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default EquiposDestacados;